import { useToast } from "@shared/hooks/use-toast";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { Alert, AlertDescription, AlertTitle } from "@shared/ui/alert";
import { PAGE_SIZE } from "@shared/utils/constants";
import { FileText, Info, Mail, MessageSquare, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useConsumerList, useDownloadBill, useViewBill } from "../../hooks";
import { ActionButton } from "./ActionButton";
import ConsumerDashboard from "./consumerDashboard";

// Channel display configuration with icons
const channelConfig = {
  Email: { icon: Mail, label: "Email", color: "text-blue-500" },
  SMS: { icon: MessageSquare, label: "SMS", color: "text-green-500" },
  Paper: { icon: FileText, label: "Paper Bill", color: "text-amber-500" },
  WhatsApp: { icon: Phone, label: "WhatsApp", color: "text-emerald-500" },
};

interface ConsumerData {
  id: number;
  billNumber: string;
  billCycleName: string;
  billAmount: number;
  consumerName: string;
  consumerNumber: string;
  isEmailChannel: boolean;
  isPaperBillChannel: boolean;
  isEmailSent: boolean;
  isPaperBillSent: boolean;
  isEmailDelivered: boolean | null;
  isPaperBillDelivered: boolean | null;
  isEmailFailed: boolean;
}

interface ConsumerTableProps {
  activeFilter: string | null;
}

export function ConsumerTable({ activeFilter }: ConsumerTableProps) {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  // Read URL params
  const searchQuery = searchParams.get("query") || "";
  const filterParam = searchParams.get("filter") || "all";
  const sortFieldParam = searchParams.get("sortField") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  // Build API filters
  const apiFilters = {
    bill_run_id: params?.id,
    page: currentPage,
    pageSize: PAGE_SIZE,
    search_data: searchQuery.length > 2 ? searchQuery : "",
    filter: filterParam,
    sort_field: sortFieldParam,
  };

  const { data, refetch } = useConsumerList(apiFilters);
  const currency = useCurrency();
  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on API data
  useEffect(() => {
    const totalItems = data?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [data?.count, currentPage]);

  // Reset page when activeFilter changes
  useEffect(() => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };
    delete newParams.page;
    setSearchParams(newParams);
  }, [activeFilter]);

  // Helper function to get channels based on data
  const getChannels = (
    consumer: ConsumerData,
    type: "opted" | "sent" | "delivered" | "failed"
  ) => {
    const channels: string[] = [];

    switch (type) {
      case "opted":
        if (consumer.isEmailChannel) channels.push("Email");
        if (consumer.isPaperBillChannel) channels.push("Paper");
        break;
      case "sent":
        if (consumer.isEmailSent) channels.push("Email");
        if (consumer.isPaperBillSent) channels.push("Paper");
        break;
      case "delivered":
        if (consumer.isEmailDelivered === true) channels.push("Email");
        if (consumer.isPaperBillDelivered === true) channels.push("Paper");
        break;
      case "failed":
        if (consumer.isEmailFailed) channels.push("Email");
        // Add paper bill failed logic if available in API
        break;
    }

    return channels;
  };

  // Helper function to render channel icons with tooltips
  const renderChannelIcons = (channels: string[], className: string = "") => {
    if (channels.length === 0) return "-";

    return (
      <div className="flex flex-wrap gap-1">
        {channels.map((channel) => {
          const config = channelConfig[channel as keyof typeof channelConfig];
          if (!config) return null;

          const IconComponent = config.icon;

          return (
            <TooltipProvider key={channel}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`rounded-md p-1 ${className} hover:bg-muted cursor-help`}
                  >
                    <IconComponent className={`h-4 w-4 ${config.color}`} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{config.label}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  };

  // Handle search
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.query = searchTerm.trim();
    } else {
      delete newParams.query;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Handle export
  const handleExport = () => {
    toast({
      title: "Export initiated",
      description: "Consumer data is being prepared for download",
    });
  };

  // Handle view click
  const handleViewClick = (billId: number) => {
    viewBill.mutateAsync(billId.toString());
  };

  // Handle download click
  const handleDownloadClick = (billId: number, filename?: string) => {
    const id = billId.toString();
    downloadBill.mutateAsync({
      billId: id,
      filename: filename || `bill-${billId}.pdf`,
    });
  };

  // Define table columns
  const columns: TableColumn<ConsumerData>[] = [
    {
      key: "consumerName",
      header: "Consumer Name",
      sortable: true,
      render: (value: string, row: ConsumerData) => {
        const missingContactInfo =
          !row.isEmailChannel && !row.isPaperBillChannel;
        return (
          <div className="font-medium flex items-center gap-1">
            {value}
            {missingContactInfo && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Missing contact information</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
    },
    {
      key: "consumerNumber",
      header: "Consumer No",
    },
    {
      key: "billNumber",
      sortable: true,
      header: "Bill Number",
    },
    {
      key: "billAmount",
      sortable: true,
      header: "Bill Amount",
      render: (value: number) => (
        <div className="font-medium">
          {currency} {value}
        </div>
      ),
    },
    {
      key: "optedChannels",
      header: "Opted Channels",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "opted");
        return renderChannelIcons(channels);
      },
    },
    {
      key: "sentChannels",
      header: "Sent",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "sent");
        return renderChannelIcons(channels);
      },
    },
    {
      key: "deliveredChannels",
      header: "Delivered",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "delivered");
        return renderChannelIcons(channels, "bg-green-100");
      },
    },
    {
      key: "readChannels",
      header: "Read",
      render: (value: any, row: ConsumerData) => {
        // Add read logic when available in API
        return renderChannelIcons([], "bg-blue-100");
      },
    },
    {
      key: "failedChannels",
      header: "Failed",
      render: (value: any, row: ConsumerData) => {
        const channels = getChannels(row, "failed");
        return renderChannelIcons(channels, "bg-red-100");
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (value: any, row: ConsumerData) => {
        return (
          <div className="flex flex-row gap-1">
            <ActionButton
              type="view"
              title="View Bill"
              onClick={() => handleViewClick(row.id)}
              iconOnly
            />
            <ActionButton
              type="download"
              title="Download Bill"
              onClick={() => handleDownloadClick(row.id)}
              iconOnly
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <ConsumerDashboard />
      <Alert variant="default" className="bg-muted border border-input mb-4">
        <Info className="h-4 w-4" />
        <AlertTitle>Tip</AlertTitle>
        <AlertDescription className="text-xs">
          Each row shows consumer bill info and distribution status per channel.
          Click 'Actions' to view or download the individual bill.
        </AlertDescription>
      </Alert>
      
          <GenericTable
            data={data?.result ?? []}
            columns={columns}
            rowKey="id"
            emptyMessage="No consumers found"
            pagination={pagination}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            onExport={handleExport}
            searchPlaceholder="Search by consumer name, ID, or bill number..."
          />
    </div>
  );
}
